import { Box, Flex, Text } from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AgencyContext } from "../AgencyProvider/AgencyProvider";
import { BillingOptions } from "./BillingTabs";
import BillingCard from "../BillingCard/BillingCard";
import { computeUnionByGroup } from "./utils";

// Normalize a raw billing-plan row (as returned from /effective_billing_plan
// and wrapped by fetchCouponPlan) into the same shape processBillingPlans
// produces — so it slots into the comparison row alongside other cards.
function normalizeExtraPlanAsListItem(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    plan: raw.plan_lookup_key,
    isActive: true,         // it's the user's current plan
    label: raw.label || "Activated",
    isDisable: true,        // can't "switch" to your own plan
    features: raw.features,
    structuredFeatures: raw.structuredFeatures,
    pricing: raw.description,
    popular: !!raw.popular,
    name: raw.name,
    is_trial: false,
    description: raw.description,
    erlyBirdsOffer: "",
  };
}

// (bots_count, words_count) tuple for slotting an extra plan among
// API-ordered plans. structuredFeatures.byKey carries the raw values
// produced by getStructuredFeatures.
function sortPair(plan) {
  const b = plan?.structuredFeatures?.byKey?.bots_count;
  const w = plan?.structuredFeatures?.byKey?.words_count;
  return [typeof b === "number" ? b : 0, typeof w === "number" ? w : 0];
}

const PlansSection = ({
  billingPlans,
  extraActivePlans = [],
  isSalesBtnisvisible,
  userDetails,
}) => {
  const agency = useContext(AgencyContext);
  const { textColor, cardBg, buttonColorScheme } = agency;
  const [selectedTab, setSelectedTab] = useState("endUser");

  // Club the user's current plan (fetched separately from
  // /effective_billing_plan whenever it isn't in the standard list — e.g.
  // hidden / coupon / lifetime) into the comparison row. Preserve the
  // API's order of `billingPlans` for the listed plans; SLOT each
  // extra at its sorted position by (bots_count, words_count) so it
  // doesn't always appear last regardless of size.
  const mergedBillingPlans = useMemo(() => {
    if (!extraActivePlans || extraActivePlans.length === 0) return billingPlans;
    const existing = new Set(billingPlans.map((p) => p.id));
    const extras = extraActivePlans
      .filter((p) => p && p.id && !existing.has(p.id))
      .map(normalizeExtraPlanAsListItem)
      .filter(Boolean);
    if (extras.length === 0) return billingPlans;

    const result = [...billingPlans];
    for (const extra of extras) {
      const [eb, ew] = sortPair(extra);
      let insertAt = result.length;
      for (let i = 0; i < result.length; i++) {
        const [b, w] = sortPair(result[i]);
        if (b > eb || (b === eb && w > ew)) {
          insertAt = i;
          break;
        }
      }
      result.splice(insertAt, 0, extra);
    }
    return result;
  }, [billingPlans, extraActivePlans]);

  const activePlan = mergedBillingPlans.find(
    (plan) => plan.id === userDetails.active_billing_plan_id
  );
  useEffect(() => {
    if (activePlan) {
      const isAgencyPlan = activePlan.features?.[0]?.Agency === 1;
      setSelectedTab(isAgencyPlan ? "agency" : "endUser");
    }
  }, [activePlan]);

  const agencyUserPlans = useMemo(() => {
    return mergedBillingPlans.filter((plan) => {
      const agencyFeature = plan.features?.[0]?.Agency;
      return selectedTab === "endUser" ? !agencyFeature : agencyFeature === 1;
    });
  }, [mergedBillingPlans, selectedTab]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mt={2}
    >
      <Box>
        {isSalesBtnisvisible && (
          <BillingOptions
            selectedOption={selectedTab}
            setSelectedOption={setSelectedTab}
          />
        )}
      </Box>
      <Flex
        width="90%"
        gap={3}
        justifyContent="center"
        align="stretch"
        m="auto"
        alignContent={"center"}
      >
        {(() => {
          const plansToRender = isSalesBtnisvisible ? agencyUserPlans : mergedBillingPlans;
          // Union of features activated in at least one of these plans —
          // every card renders the same row set in the same order so the
          // eye scans straight across columns. Features no plan has are
          // dropped entirely.
          const unionByGroup = computeUnionByGroup(plansToRender);
          return plansToRender.map((item, index) => (
            <BillingCard
              key={index}
              features={item?.features}
              structuredFeatures={item?.structuredFeatures}
              unionByGroup={unionByGroup}
              // Delta comparison: previous (lower) plan's raw key→value
              // map so the card can flag new-in-this-tier items.
              previousByKey={
                index > 0
                  ? plansToRender[index - 1]?.structuredFeatures?.byKey
                  : null
              }
              popular={item.popular}
              active={item.isActive}
              planName={item.name}
              lookup_key={item.plan}
              pricing={item.pricing}
              item={item}
              label={item.label}
              erlyBirdsOffer={item.erlyBirdsOffer}
              isDisable={item.isDisable}
              isTrial={item.is_trial}
              bg={cardBg}
              color={textColor}
              buttonColorScheme={buttonColorScheme}
            />
          ));
        })()}
      </Flex>
      <Text fontSize="18px" p={3}>
        {userDetails.is_trial && <>You're currently using our trial.</>}
      </Text>
    </Box>
  );
};

export default PlansSection;
